// src/components/DocumentForgery/Analysis/DocumentAnalysis.jsx
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import { Document, Page, pdfjs } from 'react-pdf';
import {
  FiArrowLeft, FiShield, FiAlertTriangle, FiCheckCircle, FiXCircle,
  FiClock, FiZap, FiFileText, FiHash, FiAlertOctagon, FiActivity,
  FiInfo, FiPackage, FiMaximize2, FiExternalLink, FiCopy,
  FiMessageSquare, FiBookmark, FiToggleLeft, FiToggleRight, FiSave, FiSettings,
} from 'react-icons/fi';
import { docAnalysisHandler, updateDocCommentHandler, updateDocStatusHandler } from '../../../services/documentForgery';
import { showErrorToast } from '../../../utility/helper';
import './style.scss';

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

// ── Helpers ───────────────────────────────────────────────────────────────────
const formatLabel = (t) => {
  if (!t) return '';
  return t.split(/[_\s]+/).map(w =>
    w === w.toUpperCase() && w.length > 1
      ? w
      : w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()
  ).join(' ');
};

const formatDate = (iso) => {
  if (!iso) return null;
  try {
    return new Intl.DateTimeFormat('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    }).format(new Date(iso));
  } catch { return iso; }
};

const getFileFormat = (name) => {
  if (!name) return 'Unknown';
  return (name.split('.').pop() || 'Unknown').toUpperCase();
};

const riskToSeverity = (score) => {
  if (!score && score !== 0) return 'medium';
  if (score >= 90) return 'critical';
  if (score >= 70) return 'high';
  if (score >= 40) return 'medium';
  return 'low';
};

const normaliseIssues = (issues = []) => issues.map(issue => ({
  issue_type:   issue.issue_type,
  explanation:  issue.explanation,
  risk_score:   issue.risk_score,
  tags:         issue.tags || [],
  bounding_box: issue.bounding_box || null,
  created_at:   issue.created_at,
  severity:     riskToSeverity(issue.risk_score),
  message:      issue.explanation,
}));

// Detect what kind of data a QR/barcode contains
const analyseQrData = (data) => {
  if (!data) return { type: 'unknown', label: 'Unknown', risk: null, detail: null };
  const d = data.trim();

  if (/^https?:\/\//i.test(d)) {
    // Check for suspicious URL patterns
    const suspicious = /bit\.ly|tinyurl|t\.co|goo\.gl|ow\.ly|shorturl/i.test(d)
      || /[^\x00-\x7F]/.test(d)   // non-ASCII in URL
      || d.length > 200;
    return {
      type:   'url',
      label:  'URL',
      risk:   suspicious ? 'medium' : 'low',
      detail: suspicious ? 'Shortened or suspicious URL — verify before opening' : 'Standard URL',
      icon:   '🔗',
    };
  }
  if (/^upi:\/\//i.test(d) || /pa=|pn=|am=|tn=/i.test(d)) {
    return { type: 'upi', label: 'UPI Payment', risk: 'high', icon: '💳',
      detail: 'UPI payment link — verify payee name and VPA match invoice vendor' };
  }
  if (/^\d{15,17}$/.test(d.replace(/\s/g, ''))) {
    return { type: 'gstin_like', label: 'Numeric Code', risk: 'low', icon: '#',
      detail: 'Long numeric code' };
  }
  if (/^[0-9A-Z]{15}$/i.test(d.replace(/\s/g, ''))) {
    return { type: 'gstin', label: 'GSTIN-like', risk: 'low', icon: '🏢',
      detail: 'Possible GSTIN or registration number' };
  }
  if (/invoice|bill|gst|tax|amount|total|vendor/i.test(d)) {
    return { type: 'invoice_data', label: 'Invoice Data', risk: 'low', icon: '📄',
      detail: 'Structured invoice information encoded in QR' };
  }
  if (d.startsWith('{') || d.startsWith('[')) {
    return { type: 'json', label: 'JSON Data', risk: 'low', icon: '{}',
      detail: 'Structured JSON payload' };
  }
  if (/^\d+$/.test(d)) {
    return { type: 'numeric', label: 'Numeric', risk: null, icon: '#',
      detail: 'Numeric code' };
  }
  return { type: 'text', label: 'Plain Text', risk: null, icon: '📝', detail: 'Plain text data' };
};


// ── Smart bounding-box tooltip ────────────────────────────────────────────────
// Renders above or below the box based on vertical position in the container.
// Always has a readable min-width so text never collapses into a single column.
//
// Props:
//   text        — main text to display
//   subtext     — optional dim line above main text (e.g. QR type)
//   boxTop      — top px of the parent bbox element (absolute within container)
//   containerH  — total scrollable height of the page/image container
//   alignLeft   — if true, tooltip left-aligns; if false, tries to stay in view
const BBoxTooltip = ({ text, subtext, boxTop, containerH, boxLeft, containerW }) => {
  // Show above if box is in bottom 40% of container, else below
  const showAbove = containerH > 0 && boxTop > containerH * 0.6;

  // Clamp tooltip horizontally so it doesn't overflow right edge
  // Tooltip is 240px wide; if box starts past containerW-260, anchor to right
  const anchorRight = containerW > 0 && boxLeft > containerW - 260;

  const posStyle = showAbove
    ? { bottom: '100%', top: 'auto',   marginBottom: 6, marginTop: 0    }
    : { top:    '100%', bottom: 'auto', marginTop:    6, marginBottom: 0 };

  const horzStyle = anchorRight
    ? { right: 0, left: 'auto' }
    : { left:  0, right: 'auto' };

  return (
    <div style={{
      position: 'absolute',
      ...posStyle,
      ...horzStyle,
      // ── Width: never narrower than 220px, never wider than 300px ──────────
      minWidth:  220,
      maxWidth:  300,
      width:     'max-content',   // grows to fit text up to maxWidth
      // ── Typography: wrap words, never nowrap ──────────────────────────────
      whiteSpace:   'normal',
      wordBreak:    'break-word',
      overflowWrap: 'break-word',
      // ── Appearance ────────────────────────────────────────────────────────
      background:   '#1f2937',
      color:        '#fff',
      padding:      '7px 10px',
      borderRadius: 6,
      fontSize:     11,
      fontWeight:   400,
      lineHeight:   1.55,
      boxShadow:    '0 4px 12px rgba(0,0,0,0.35)',
      pointerEvents:'none',
      zIndex:       50,
    }}>
      {subtext && (
        <div style={{ color: '#94a3b8', fontSize: 10, fontWeight: 600, marginBottom: 3 }}>
          {subtext}
        </div>
      )}
      {text}
    </div>
  );
};

// ── Severity config ───────────────────────────────────────────────────────────
const SEV_CFG = {
  critical: { color: '#b91c1c', bg: '#fff1f2', border: '#fecaca', icon: <FiAlertOctagon /> },
  high:     { color: '#ef4444', bg: '#fef2f2', border: '#fecaca', icon: <FiXCircle />     },
  medium:   { color: '#f59e0b', bg: '#fffbeb', border: '#fde68a', icon: <FiAlertTriangle />},
  low:      { color: '#10b981', bg: '#f0fdf4', border: '#bbf7d0', icon: <FiCheckCircle /> },
};
const getCfg = (val) =>
  SEV_CFG[val?.toLowerCase()] ||
  { color: '#6366f1', bg: '#eef2ff', border: '#c7d2fe', icon: <FiShield /> };

const StatusBadge = ({ value, size = 'md' }) => {
  if (!value) return <span style={{ color: '#9ca3af', fontSize: 12 }}>—</span>;
  const cfg = getCfg(value);
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      background: cfg.bg, color: cfg.color,
      border: `1px solid ${cfg.border}`,
      borderRadius: 20, fontWeight: 700,
      padding: size === 'sm' ? '2px 8px' : '4px 12px',
      fontSize: size === 'sm' ? 11 : 12, whiteSpace: 'nowrap',
    }}>
      <span style={{ display: 'flex', fontSize: size === 'sm' ? 11 : 12 }}>{cfg.icon}</span>
      {formatLabel(value)}
    </span>
  );
};

const RiskScoreRing = ({ score }) => {
  const n = Math.min(Math.max(parseFloat(score) || 0, 0), 100);
  const color = n >= 70 ? '#ef4444' : n >= 40 ? '#f59e0b' : '#10b981';
  const label = n >= 70 ? 'HIGH' : n >= 40 ? 'MEDIUM' : 'LOW';
  const r = 30, circ = 2 * Math.PI * r;
  const filled = (n / 100) * circ;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 4 }}>
      <div style={{ position: 'relative', width: 76, height: 76, flexShrink: 0 }}>
        <svg width="76" height="76" style={{ transform: 'rotate(-90deg)' }}>
          <circle cx="38" cy="38" r={r} fill="none" stroke="#e5e7eb" strokeWidth="7" />
          <circle cx="38" cy="38" r={r} fill="none" stroke={color} strokeWidth="7"
            strokeDasharray={`${filled} ${circ - filled}`} strokeLinecap="round"
            style={{ transition: 'stroke-dasharray 0.7s ease' }}
          />
        </svg>
        <div style={{
          position: 'absolute', inset: 0, display: 'flex',
          alignItems: 'center', justifyContent: 'center',
          fontSize: 17, fontWeight: 800, color,
        }}>
          {n > 0 ? Math.round(n) : '—'}
        </div>
      </div>
      <div>
        <div style={{ fontSize: 11, color: '#6b7280', fontWeight: 500, marginBottom: 5 }}>Risk Score</div>
        <StatusBadge value={label} size="sm" />
      </div>
    </div>
  );
};

const InfoRow = ({ icon, label, value, valueNode }) => {
  if (!value && !valueNode) return null;
  return (
    <div style={{ padding: '5px 0', borderBottom: '1px solid #f3f4f6' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 2 }}>
        <span style={{ fontSize: 11, color: '#9ca3af', display: 'flex' }}>{icon}</span>
        <span style={{ fontSize: 10, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.4px' }}>{label}</span>
      </div>
      <div style={{ paddingLeft: 18 }}>
        {valueNode || <span style={{ fontSize: 12, fontWeight: 600, color: '#111827', wordBreak: 'break-word' }}>{value}</span>}
      </div>
    </div>
  );
};

const SCard = ({ title, children, style, accent }) => (
  <div style={{
    background: accent ? `${accent}08` : '#f9fafb',
    border: `1px solid ${accent ? `${accent}33` : '#e5e7eb'}`,
    borderRadius: 8, padding: 12, ...style,
  }}>
    {title && (
      <div style={{ fontSize: 10, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.6px', color: accent || '#9ca3af', marginBottom: 10 }}>
        {title}
      </div>
    )}
    {children}
  </div>
);

const PanelToggle = ({ value, onChange, options }) => (
  <div style={{ display: 'flex', background: '#f3f4f6', borderRadius: 7, padding: 3, gap: 3 }}>
    {options.map(opt => (
      <button key={opt.value} onClick={() => onChange(opt.value)} style={{
        padding: '5px 10px', borderRadius: 5, border: 'none',
        fontSize: 11, fontWeight: 600, cursor: 'pointer',
        background: value === opt.value ? '#fff' : 'transparent',
        color: value === opt.value ? '#111827' : '#9ca3af',
        boxShadow: value === opt.value ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
        transition: 'all 0.15s', display: 'flex', alignItems: 'center', gap: 4,
        position: 'relative',
      }}>
        {opt.icon}{opt.label}
        {opt.badge > 0 && (
          <span style={{
            background: '#ef4444', color: '#fff',
            borderRadius: 99, fontSize: 9, fontWeight: 800,
            padding: '1px 5px', lineHeight: 1.4,
          }}>{opt.badge}</span>
        )}
      </button>
    ))}
  </div>
);

// ── Feature value renderer ────────────────────────────────────────────────────
const FieldValue = ({ v }) => {
  if (v === null || v === undefined)
    return <span style={{ color: '#d1d5db', fontStyle: 'italic', fontSize: 11 }}>—</span>;
  if (typeof v === 'boolean')
    return <span style={{ color: v ? '#10b981' : '#ef4444', fontWeight: 700, fontSize: 11 }}>{v.toString()}</span>;
  if (Array.isArray(v)) {
    if (v.length === 0)
      return <span style={{ color: '#d1d5db', fontStyle: 'italic', fontSize: 11 }}>empty</span>;
    return (
      <div style={{ marginTop: 4 }}>
        {v.map((item, i) => (
          <div key={i} style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 5, padding: '5px 8px', marginBottom: 4, fontSize: 11 }}>
            {typeof item === 'object'
              ? Object.entries(item).map(([k, val]) => (
                  <div key={k} style={{ display: 'flex', justifyContent: 'space-between', gap: 8, padding: '1px 0', flexWrap: 'wrap' }}>
                    <span style={{ color: '#6b7280', fontSize: 10, flexShrink: 0 }}>{formatLabel(k)}</span>
                    <span style={{ fontWeight: 600, color: '#111827', fontSize: 10, textAlign: 'right', wordBreak: 'break-word', maxWidth: '60%' }}>
                      {String(val ?? '—')}
                    </span>
                  </div>
                ))
              : <span style={{ wordBreak: 'break-word' }}>{String(item)}</span>
            }
          </div>
        ))}
      </div>
    );
  }
  if (typeof v === 'object') {
    return (
      <div style={{ paddingLeft: 8, marginTop: 2 }}>
        {Object.entries(v).map(([k, val]) => (
          <div key={k} style={{ display: 'flex', justifyContent: 'space-between', gap: 8, padding: '2px 0', borderBottom: '1px solid #f9fafb', flexWrap: 'wrap' }}>
            <span style={{ color: '#6b7280', fontSize: 10, flexShrink: 0 }}>{formatLabel(k)}</span>
            <FieldValue v={val} />
          </div>
        ))}
      </div>
    );
  }
  return <span style={{ fontSize: 11, fontWeight: 600, color: '#111827', wordBreak: 'break-word', display: 'block' }}>{String(v)}</span>;
};

const FeaturesPanel = ({ features, clientFeatures }) => {
  // Exclude qr_barcodes from the features panel — shown separately
  const displayFeatures = Object.fromEntries(
    Object.entries(features || {}).filter(([k]) => k !== 'qr_barcodes')
  );
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {Object.entries(displayFeatures).map(([key, val]) => (
        <div key={key} style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 7, padding: '8px 10px', overflow: 'hidden' }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: '#44ace4', textTransform: 'uppercase', letterSpacing: '0.3px', marginBottom: 4 }}>
            {formatLabel(key)}
          </div>
          <FieldValue v={val} />
        </div>
      ))}
      {Object.keys(clientFeatures || {}).length > 0 && (
        <>
          <div style={{ fontSize: 10, color: '#9ca3af', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', marginTop: 4 }}>
            Client fields
          </div>
          {Object.entries(clientFeatures || {}).map(([key, val]) => (
            <div key={key} style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 7, padding: '8px 10px', overflow: 'hidden' }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: '#92400e', textTransform: 'uppercase', letterSpacing: '0.3px', marginBottom: 4 }}>
                {formatLabel(key)}
              </div>
              <FieldValue v={val} />
            </div>
          ))}
        </>
      )}
    </div>
  );
};

const MetadataPanel = ({ metadata = [] }) => {
  if (!metadata.length) return (
    <div style={{ textAlign: 'center', color: '#9ca3af', padding: 20, fontSize: 12 }}>No metadata available</div>
  );
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {metadata.map((m, i) => (
        <div key={i} style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 7, padding: '10px 12px' }}>
          <InfoRow icon={<FiClock />}    label="File created"   value={formatDate(m.create_date)} />
          <InfoRow icon={<FiClock />}    label="Last modified"  value={formatDate(m.modify_date)} />
          <InfoRow icon={<FiActivity />} label="Software"       value={m.software_used} />
          <InfoRow icon={<FiActivity />} label="Producer"       value={m.producer} />
          <InfoRow icon={<FiHash />}     label="Author"         value={m.author} />
          {m.date_mismatch_flag && (
            <div style={{ marginTop: 6, padding: '4px 8px', background: '#fff1f2', border: '1px solid #fecaca', borderRadius: 5, fontSize: 11, color: '#b91c1c', fontWeight: 600 }}>
              ⚠ Date mismatch detected
            </div>
          )}
          {(m.flags || []).length > 0 && (
            <div style={{ marginTop: 6, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
              {m.flags.map((f, fi) => (
                <span key={fi} style={{ fontSize: 10, padding: '2px 7px', borderRadius: 20, background: '#fff1f2', color: '#b91c1c', border: '1px solid #fecaca', fontWeight: 600 }}>
                  {formatLabel(f)}
                </span>
              ))}
            </div>
          )}
          <InfoRow icon={<FiShield />} label="Metadata risk score"
            valueNode={
              <span style={{ fontSize: 12, fontWeight: 700, color: m.risk_score >= 70 ? '#ef4444' : m.risk_score >= 40 ? '#f59e0b' : '#10b981' }}>
                {m.risk_score ?? '—'}
              </span>
            }
          />
        </div>
      ))}
    </div>
  );
};

// ── QR / Barcode Panel ────────────────────────────────────────────────────────
const QRBarcodePanel = ({ qrBarcodes = [], onHoverBox, activeBoxIdx }) => {
  const [copied, setCopied] = useState(null);

  const handleCopy = (text, idx) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(idx);
      setTimeout(() => setCopied(null), 1500);
    });
  };

  if (!qrBarcodes.length) return (
    <div style={{ textAlign: 'center', color: '#9ca3af', padding: '28px 16px' }}>
      <div style={{ fontSize: 28, marginBottom: 8 }}>🔍</div>
      <div style={{ fontSize: 12, fontWeight: 600 }}>No QR codes or barcodes detected</div>
      <div style={{ fontSize: 11, marginTop: 4 }}>The document was scanned but no QR/barcode was found</div>
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {/* Summary header */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '8px 10px', background: '#f0f9ff',
        border: '1px solid #bae6fd', borderRadius: 7, marginBottom: 4,
      }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: '#0369a1' }}>
          {qrBarcodes.length} code{qrBarcodes.length > 1 ? 's' : ''} detected
        </span>
        <span style={{ fontSize: 10, color: '#0284c7' }}>
          {qrBarcodes.filter(q => analyseQrData(q.data).risk === 'high').length > 0
            ? '⚠ Review required'
            : '✓ Scan complete'}
        </span>
      </div>

      {qrBarcodes.map((qr, idx) => {
        const analysis = analyseQrData(qr.data);
        const riskCfg  = analysis.risk ? getCfg(analysis.risk) : null;
        const isActive = activeBoxIdx === `qr-${idx}`;
        const isUrl    = analysis.type === 'url';
        const isUpi    = analysis.type === 'upi';

        return (
          <div
            key={idx}
            onMouseEnter={() => onHoverBox?.(`qr-${idx}`, qr.bounding_box)}
            onMouseLeave={() => onHoverBox?.(null, null)}
            style={{
              background: '#fff',
              border: `1px solid ${isActive ? '#44ace4' : '#e5e7eb'}`,
              borderLeft: `3px solid ${riskCfg ? riskCfg.color : '#44ace4'}`,
              borderRadius: 7,
              padding: '10px 12px',
              cursor: 'default',
              transition: 'border-color 0.15s, box-shadow 0.15s',
              boxShadow: isActive ? '0 0 0 2px #44ace444' : 'none',
            }}
          >
            {/* Header row */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ fontSize: 14 }}>{analysis.icon || '▦'}</span>
                <div>
                  <span style={{ fontSize: 12, fontWeight: 700, color: '#111827' }}>
                    {qr.type}
                  </span>
                  <span style={{
                    marginLeft: 6, fontSize: 10, fontWeight: 600,
                    background: '#f0f7ff', color: '#44ace4',
                    border: '1px solid #dbeafe', borderRadius: 20,
                    padding: '1px 6px',
                  }}>
                    {analysis.label}
                  </span>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                {qr.page && (
                  <span style={{ fontSize: 10, color: '#9ca3af' }}>p.{qr.page}</span>
                )}
                {riskCfg && (
                  <StatusBadge value={analysis.risk} size="sm" />
                )}
              </div>
            </div>

            {/* Risk detail banner */}
            {analysis.risk && analysis.detail && (
              <div style={{
                padding: '4px 8px', borderRadius: 5, marginBottom: 7,
                background: riskCfg.bg, border: `1px solid ${riskCfg.border}`,
                fontSize: 10, color: riskCfg.color, fontWeight: 500,
              }}>
                {analysis.risk === 'high' ? '⚠ ' : 'ℹ '}{analysis.detail}
              </div>
            )}

            {/* Data content */}
            <div style={{
              background: '#f8fafc', border: '1px solid #e2e8f0',
              borderRadius: 5, padding: '6px 8px',
              fontFamily: 'monospace', fontSize: 11,
              color: '#374151', wordBreak: 'break-all',
              maxHeight: 80, overflowY: 'auto',
              lineHeight: 1.5,
            }}>
              {qr.data}
            </div>

            {/* Action buttons */}
            <div style={{ display: 'flex', gap: 6, marginTop: 7 }}>
              <button
                onClick={() => handleCopy(qr.data, idx)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 4,
                  fontSize: 10, fontWeight: 600, padding: '3px 8px',
                  background: copied === idx ? '#f0fdf4' : '#f8fafc',
                  border: `1px solid ${copied === idx ? '#bbf7d0' : '#e2e8f0'}`,
                  color: copied === idx ? '#10b981' : '#6b7280',
                  borderRadius: 5, cursor: 'pointer',
                }}
              >
                <FiCopy style={{ fontSize: 10 }} />
                {copied === idx ? 'Copied!' : 'Copy'}
              </button>

              {isUrl && (
                <button
                  onClick={() => window.open(qr.data, '_blank', 'noopener,noreferrer')}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 4,
                    fontSize: 10, fontWeight: 600, padding: '3px 8px',
                    background: '#fef3c7', border: '1px solid #fde68a',
                    color: '#92400e', borderRadius: 5, cursor: 'pointer',
                  }}
                >
                  <FiExternalLink style={{ fontSize: 10 }} />
                  Open URL
                </button>
              )}

              {/* Locate on document button */}
              {qr.bounding_box && (
                <button
                  onClick={() => onHoverBox?.(`qr-${idx}`, qr.bounding_box)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 4,
                    fontSize: 10, fontWeight: 600, padding: '3px 8px',
                    background: '#f0f9ff', border: '1px solid #bae6fd',
                    color: '#0284c7', borderRadius: 5, cursor: 'pointer', marginLeft: 'auto',
                  }}
                >
                  <FiMaximize2 style={{ fontSize: 10 }} />
                  Locate
                </button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ── PDF Viewer ────────────────────────────────────────────────────────────────
const PDFViewerWithBoxes = ({ fileUrl, issues, qrBarcodes, activeIssueIdx, activeQrBox, onBoxHover, onQrHover, visibleSeverities }) => {
  const _visSet = visibleSeverities || new Set(['critical','high','medium','low']);
  const [numPages,       setNumPages]       = useState(null);
  const [containerWidth, setContainerWidth] = useState(600);
  const [pageHeights,    setPageHeights]    = useState({});
  const containerRef = useRef(null);

  useEffect(() => {
    const obs = new ResizeObserver(entries => {
      if (entries[0]) setContainerWidth(entries[0].contentRect.width);
    });
    if (containerRef.current) obs.observe(containerRef.current);
    return () => obs.disconnect();
  }, []);

  const boxedIssues = issues.filter(i => i.bounding_box && _visSet.has(i.severity));
  const boxedQr     = (qrBarcodes || []).filter(q => q.bounding_box);

  return (
    <div ref={containerRef} style={{ width: '100%' }}>
      <Document
        file={fileUrl}
        onLoadSuccess={({ numPages: n }) => setNumPages(n)}
        onLoadError={(err) => console.error('PDF load error:', err)}
      >
        {Array.from({ length: numPages || 0 }, (_, i) => {
          const pageNum   = i + 1;
          const renderedH = pageHeights[pageNum] || 0;
          const pageIssues = boxedIssues.filter(issue => (issue.bounding_box?.page ?? 1) === pageNum);
          const pageQr     = boxedQr.filter(q => (q.bounding_box?.page ?? 1) === pageNum);

          return (
            <div key={pageNum} style={{ position: 'relative', marginBottom: 16 }}>
              <Page
                pageNumber={pageNum}
                width={containerWidth}
                onRenderSuccess={(page) => {
                  const vp = page.getViewport({ scale: 1 });
                  const scale = containerWidth / vp.width;
                  setPageHeights(prev => ({ ...prev, [pageNum]: vp.height * scale }));
                }}
              />

              {renderedH > 0 && (<>
                {/* Issue bounding boxes */}
                {pageIssues.map((issue, idx) => {
                  const bb     = issue.bounding_box;
                  const left   = bb.x1 * containerWidth;
                  const top    = bb.y1 * renderedH;
                  const width  = (bb.x2 - bb.x1) * containerWidth;
                  const height = (bb.y2 - bb.y1) * renderedH;
                  const cfg    = getCfg(issue.severity);
                  const origIdx = issues.indexOf(issue);
                  const active  = activeIssueIdx === origIdx;
                  return (
                    <div key={`issue-${idx}`}
                      onMouseEnter={() => onBoxHover(origIdx)}
                      onMouseLeave={() => onBoxHover(null)}
                      style={{
                        position: 'absolute',
                        left: Math.max(left, 0), top: Math.max(top, 0),
                        width: Math.max(width, 20), height: Math.max(height, 14),
                        border: `2px solid ${cfg.color}`,
                        background: `${cfg.color}1A`,
                        borderRadius: 3, cursor: 'pointer', zIndex: 10,
                        boxShadow: active ? `0 0 0 3px ${cfg.color}44` : 'none',
                        transition: 'box-shadow 0.15s',
                      }}
                    >
                      {active && (
                        <BBoxTooltip
                          text={issue.explanation?.slice(0, 140) + (issue.explanation?.length > 140 ? '…' : '')}
                          boxTop={top}
                          boxLeft={left}
                          containerH={renderedH}
                          containerW={containerWidth}
                        />
                      )}
                    </div>
                  );
                })}

                {/* QR / barcode bounding boxes */}
                {pageQr.map((qr, idx) => {
                  const bb      = qr.bounding_box;
                  const left    = bb.x1 * containerWidth;
                  const top     = bb.y1 * renderedH;
                  const width   = (bb.x2 - bb.x1) * containerWidth;
                  const height  = (bb.y2 - bb.y1) * renderedH;
                  const qrIdx   = (qrBarcodes || []).indexOf(qr);
                  const qrKey   = `qr-${qrIdx}`;
                  const active  = activeQrBox === qrKey;
                  const analysis = analyseQrData(qr.data);
                  const color   = analysis.risk ? getCfg(analysis.risk).color : '#6366f1';
                  return (
                    <div key={`qr-${idx}`}
                      onMouseEnter={() => onQrHover?.(qrKey, qr.bounding_box)}
                      onMouseLeave={() => onQrHover?.(null, null)}
                      style={{
                        position: 'absolute',
                        left: Math.max(left, 0), top: Math.max(top, 0),
                        width: Math.max(width, 20), height: Math.max(height, 20),
                        border: `2px dashed ${color}`,
                        background: `${color}15`,
                        borderRadius: 4, cursor: 'pointer', zIndex: 11,
                        boxShadow: active ? `0 0 0 3px ${color}44` : 'none',
                        transition: 'box-shadow 0.15s',
                        display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-end',
                      }}
                    >
                      {/* QR label badge */}
                      <span style={{
                        fontSize: 9, fontWeight: 800, padding: '1px 5px',
                        background: color, color: '#fff',
                        borderRadius: '0 0 0 4px', lineHeight: 1.6,
                      }}>
                        {qr.type === 'QRCODE' ? 'QR' : 'BC'}
                      </span>
                      {active && (
                        <BBoxTooltip
                          subtext={qr.type}
                          text={qr.data?.slice(0, 100) + (qr.data?.length > 100 ? '…' : '')}
                          boxTop={top}
                          boxLeft={left}
                          containerH={renderedH}
                          containerW={containerWidth}
                        />
                      )}
                    </div>
                  );
                })}
              </>)}
            </div>
          );
        })}
      </Document>
    </div>
  );
};

// ── Image viewer ──────────────────────────────────────────────────────────────
const ImageViewer = ({ src, issues, qrBarcodes, activeIssueIdx, activeQrBox, onBoxHover, onQrHover, visibleSeverities }) => {
  const _visSet = visibleSeverities || new Set(['critical','high','medium','low']);
  const [imgMeta, setImgMeta] = useState({ naturalW: 1, naturalH: 1, renderW: 1, renderH: 1 });
  const boxedIssues = issues.filter(i => i.bounding_box && _visSet.has(i.severity));
  const boxedQr     = (qrBarcodes || []).filter(q => q.bounding_box);

  return (
    <div style={{ position: 'relative', display: 'inline-block', width: '100%' }}>
      <img
        src={src}
        alt="Document"
        onLoad={(e) => {
          const img = e.target;
          setImgMeta({ naturalW: img.naturalWidth, naturalH: img.naturalHeight, renderW: img.clientWidth, renderH: img.clientHeight });
        }}
        style={{ width: '100%', display: 'block', borderRadius: 8, boxShadow: '0 4px 14px rgba(0,0,0,0.1)' }}
      />

      {/* Issue boxes */}
      {boxedIssues.map((issue, i) => {
        const bb = issue.bounding_box;
        const origIdx = issues.indexOf(issue);
        const active  = activeIssueIdx === origIdx;
        const cfg     = getCfg(issue.severity);
        return (
          <div key={`issue-${i}`}
            onMouseEnter={() => onBoxHover(origIdx)}
            onMouseLeave={() => onBoxHover(null)}
            style={{
              position: 'absolute',
              left: bb.x1 * imgMeta.renderW, top: bb.y1 * imgMeta.renderH,
              width: Math.max((bb.x2 - bb.x1) * imgMeta.renderW, 24),
              height: Math.max((bb.y2 - bb.y1) * imgMeta.renderH, 16),
              border: `2px solid ${cfg.color}`, background: `${cfg.color}1A`,
              borderRadius: 3, cursor: 'pointer', zIndex: 10,
              boxShadow: active ? `0 0 0 3px ${cfg.color}44` : 'none',
              transition: 'box-shadow 0.15s',
            }}
          >
            {active && (
              <BBoxTooltip
                text={issue.explanation?.slice(0, 140) + (issue.explanation?.length > 140 ? '…' : '')}
                boxTop={bb.y1 * imgMeta.renderH}
                boxLeft={bb.x1 * imgMeta.renderW}
                containerH={imgMeta.renderH}
                containerW={imgMeta.renderW}
              />
            )}
          </div>
        );
      })}

      {/* QR / barcode boxes */}
      {boxedQr.map((qr, i) => {
        const bb       = qr.bounding_box;
        const qrIdx    = (qrBarcodes || []).indexOf(qr);
        const qrKey    = `qr-${qrIdx}`;
        const active   = activeQrBox === qrKey;
        const analysis = analyseQrData(qr.data);
        const color    = analysis.risk ? getCfg(analysis.risk).color : '#6366f1';
        return (
          <div key={`qr-${i}`}
            onMouseEnter={() => onQrHover?.(qrKey, qr.bounding_box)}
            onMouseLeave={() => onQrHover?.(null, null)}
            style={{
              position: 'absolute',
              left: bb.x1 * imgMeta.renderW, top: bb.y1 * imgMeta.renderH,
              width: Math.max((bb.x2 - bb.x1) * imgMeta.renderW, 24),
              height: Math.max((bb.y2 - bb.y1) * imgMeta.renderH, 24),
              border: `2px dashed ${color}`, background: `${color}15`,
              borderRadius: 4, cursor: 'pointer', zIndex: 11,
              boxShadow: active ? `0 0 0 3px ${color}44` : 'none',
              transition: 'box-shadow 0.15s',
              display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-end',
            }}
          >
            <span style={{ fontSize: 9, fontWeight: 800, padding: '1px 5px', background: color, color: '#fff', borderRadius: '0 0 0 4px', lineHeight: 1.6 }}>
              {qr.type === 'QRCODE' ? 'QR' : 'BC'}
            </span>
            {active && (
              <BBoxTooltip
                subtext={qr.type}
                text={qr.data?.slice(0, 100) + (qr.data?.length > 100 ? '…' : '')}
                boxTop={bb.y1 * imgMeta.renderH}
                boxLeft={bb.x1 * imgMeta.renderW}
                containerH={imgMeta.renderH}
                containerW={imgMeta.renderW}
              />
            )}
          </div>
        );
      })}
    </div>
  );
};

// ── Main component ────────────────────────────────────────────────────────────
const DocumentAnalysis = () => {
  const location    = useLocation();
  const { analysisId } = useParams();
  const navigate    = useNavigate();

  const [results,    setResults]    = useState(null);
  const [loading,    setLoading]    = useState(true);
  const [fileUrl,    setFileUrl]    = useState(null);
  const [fileType,   setFileType]   = useState(null);
  const [issues,     setIssues]     = useState([]);
  const [metadata,   setMetadata]   = useState([]);
  const [qrBarcodes, setQrBarcodes] = useState([]);

  const [activeIdx,    setActiveIdx]    = useState(null);
  const [activeQrBox,  setActiveQrBox]  = useState(null);
  const [leftTab,      setLeftTab]      = useState('features');
  const [rightTab,     setRightTab]     = useState('issues');

  // ── Review / comment state ──────────────────────────────────────────────
  const [docStatus,    setDocStatus]    = useState(null);
  const [statusSaving, setStatusSaving] = useState(false);
  const [commentOpen,  setCommentOpen]  = useState(false);
  const [commentText,  setCommentText]  = useState('');
  const [commentSaving,setCommentSaving]= useState(false);
  const [commentSaved, setCommentSaved] = useState(false);
  // ── Detection settings panel ──────────────────────────────────────────
  const [detectionOpen, setDetectionOpen] = useState(false);
  // ── Severity filter ───────────────────────────────────────────────────
  const [visibleSeverities, setVisibleSeverities] = useState(
    new Set(['critical', 'high', 'medium', 'low'])
  );
  // ── Risk summary accordion ────────────────────────────────────────────
  const [summaryOpen, setSummaryOpen] = useState(true);
  // ── Duplicate invoice ─────────────────────────────────────────────────
  const [dupOpen, setDupOpen] = useState(false);

  const issueRefs = useRef({});

  useEffect(() => {
    const state = location?.state;
    const handleData = (data) => {
      if (!data) return;
      setResults(data);
      setIssues(normaliseIssues(data.issues || []));
      setMetadata(data.metadata || []);
      setQrBarcodes((data.features_capture || {}).qr_barcodes || []);
      setDocStatus(data.status || 'pending');
      setCommentText(data.user_comments || '');

      const stateFile = state?.file;
      const url = data.doc_url || data.document_url;
      if (typeof stateFile === 'string') {
        setFileUrl(stateFile); setFileType(stateFile.includes('.pdf') ? 'pdf' : 'image');
      } else if (stateFile instanceof File) {
        setFileUrl(URL.createObjectURL(stateFile));
        setFileType(stateFile.type.includes('pdf') ? 'pdf' : 'image');
      } else {
        setFileUrl(url); setFileType((url || '').includes('.pdf') ? 'pdf' : 'image');
      }
    };

    if (state?.analysis) { handleData(state.analysis); setLoading(false); }
    else if (analysisId) {
      setLoading(true);
      docAnalysisHandler(analysisId)
        .then(res => handleData(res?.data))
        .catch(() => showErrorToast('Failed to load analysis'))
        .finally(() => setLoading(false));
    } else setLoading(false);
  }, [location.state, analysisId]);

  useEffect(() => {
    if (activeIdx !== null && issueRefs.current[activeIdx]) {
      issueRefs.current[activeIdx].scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [activeIdx]);

  const handleIssueHover = useCallback((idx) => { setActiveIdx(idx); setActiveQrBox(null); }, []);
  const handleIssueLeave = useCallback(() => setActiveIdx(null), []);
  const handleBoxHover   = useCallback((idx) => { setActiveIdx(idx); setActiveQrBox(null); }, []);
  const handleQrHover    = useCallback((key) => { setActiveQrBox(key); setActiveIdx(null); }, []);

  // ── Status toggle (Mark for Review / Accepted / Rejected) ──────────────
  const handleStatusChange = useCallback(async (newStatus) => {
    if (!results?.id || statusSaving) return;
    setStatusSaving(true);
    try {
      await updateDocStatusHandler(results.id, newStatus);
      setDocStatus(newStatus);
    } catch {
      // silent — status is low-stakes, no toast needed
    } finally {
      setStatusSaving(false);
    }
  }, [results?.id, statusSaving]);

  // ── Save comment ──────────────────────────────────────────────────────
  const handleSaveComment = useCallback(async () => {
    if (!results?.id || commentSaving) return;
    setCommentSaving(true);
    try {
      await updateDocCommentHandler(results.id, commentText);
      setCommentSaved(true);
      setTimeout(() => { setCommentSaved(false); setCommentOpen(false); }, 900);
    } catch {
      // silent fallback
    } finally {
      setCommentSaving(false);
    }
  }, [results?.id, commentText, commentSaving]);

  const riskScore   = results?.overall_risk_score;
  const riskLevel   = riskScore >= 90 ? 'CRITICAL' : riskScore >= 70 ? 'HIGH' : riskScore >= 40 ? 'MEDIUM' : 'LOW';
  const docName     = results?.name || results?.document_name;
  const summary     = results?.summary;
  const fileFormat  = getFileFormat(docName);
  const qrCount     = qrBarcodes.length;

  // Switch right panel to QR tab if qr found and user is on issues
  useEffect(() => {
    if (qrCount > 0 && rightTab === 'issues') {/* keep default — just show badge */}
  }, [qrCount]);

  if (loading) return <div className="center-state"><div className="loading-spinner" /><p>Loading analysis…</p></div>;
  if (!fileUrl) return <div className="center-state"><h3>No document found</h3><button className="btn" onClick={() => navigate(-1)}>Go Back</button></div>;

  return (
    <div className="document-analysis-wrapper">

      {/* ── Detection Settings modal ── */}
      {detectionOpen && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 200,
          background: 'rgba(0,0,0,0.4)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: 16,
        }}
          onClick={e => { if (e.target === e.currentTarget) setDetectionOpen(false); }}
        >
          <div style={{
            background: '#fff', borderRadius: 12, width: '100%', maxWidth: 480,
            maxHeight: '80vh', display: 'flex', flexDirection: 'column',
            boxShadow: '0 20px 60px rgba(0,0,0,0.22)', overflow: 'hidden',
          }}>
            {/* Header */}
            <div style={{ padding: '16px 20px 12px', borderBottom: '1px solid #f3f4f6', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontSize: 15, fontWeight: 800, color: '#111827' }}>Detection Settings Used</div>
                <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 2 }}>
                  Checks active when this document was analysed
                </div>
              </div>
              <button onClick={() => setDetectionOpen(false)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 20, color: '#9ca3af', lineHeight: 1, padding: 4 }}>×</button>
            </div>
            {/* Body */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '8px 20px 16px' }}>
              {results?.detection_flags && typeof results.detection_flags === 'object' && Object.keys(results.detection_flags).length > 0 ? (
                <>
                  {/* Group by core / advanced */}
                  {['core', 'advanced'].map(group => {
                    const FEATURE_LABELS = {
                      feature_extraction:     { label: 'Feature Extraction',     group: 'core' },
                      numeric_reconciliation: { label: 'Numeric Reconciliation', group: 'core' },
                      date_checks:            { label: 'Date Consistency',       group: 'core' },
                      duplicate_detection:    { label: 'Duplicate Line Items',   group: 'core' },
                      words_vs_figures:       { label: 'Amount in Words Check',  group: 'core' },
                      metadata_forensics:     { label: 'PDF Metadata Forensics', group: 'core' },
                      qr_barcode_scan:        { label: 'QR / Barcode Scan',      group: 'core' },
                      risk_summary:           { label: 'AI Risk Summary',        group: 'core' },
                      gst_verification:       { label: 'GST Verification',       group: 'advanced' },
                      llm_forgery_analysis:   { label: 'LLM Visual Forgery',     group: 'advanced' },
                      signature_check:        { label: 'Signature Analysis',     group: 'advanced' },
                      font_manipulation:      { label: 'Font Manipulation',      group: 'advanced' },
                      handwriting_analysis:   { label: 'Handwriting Analysis',   group: 'advanced' },
                      low_confidence_cluster: { label: 'Paste-over Detection',   group: 'advanced' },
                    };
                    const groupEntries = Object.entries(results.detection_flags)
                      .filter(([k]) => (FEATURE_LABELS[k]?.group || 'core') === group);
                    if (!groupEntries.length) return null;
                    const enabledCount = groupEntries.filter(([,v]) => v).length;
                    return (
                      <div key={group}>
                        <div style={{
                          fontSize: 10, fontWeight: 800, textTransform: 'uppercase',
                          letterSpacing: '0.5px', color: '#9ca3af',
                          padding: '12px 0 6px',
                          display: 'flex', justifyContent: 'space-between',
                          position: 'sticky', top: 0, background: '#fff',
                        }}>
                          <span>{group === 'core' ? 'Core Checks' : 'Advanced Checks'}</span>
                          <span style={{ color: enabledCount === groupEntries.length ? '#16a34a' : '#9ca3af' }}>
                            {enabledCount}/{groupEntries.length} enabled
                          </span>
                        </div>
                        {groupEntries.map(([key, enabled]) => (
                          <div key={key} style={{
                            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                            padding: '8px 0', borderBottom: '1px solid #f9fafb',
                          }}>
                            <span style={{ fontSize: 13, color: enabled ? '#111827' : '#9ca3af', fontWeight: enabled ? 500 : 400 }}>
                              {FEATURE_LABELS[key]?.label || key.replace(/_/g, ' ')}
                            </span>
                            <span style={{
                              fontSize: 10, fontWeight: 700, padding: '2px 8px',
                              borderRadius: 20,
                              background: enabled ? '#dcfce7' : '#f3f4f6',
                              color:      enabled ? '#15803d' : '#9ca3af',
                            }}>
                              {enabled ? '✓ On' : 'Off'}
                            </span>
                          </div>
                        ))}
                      </div>
                    );
                  })}
                </>
              ) : (
                <div style={{ textAlign: 'center', padding: '28px 0' }}>
                  <div style={{ fontSize: 24, marginBottom: 10 }}>⚙️</div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#374151', marginBottom: 6 }}>
                    System defaults used
                  </div>
                  <div style={{ fontSize: 12, color: '#9ca3af', lineHeight: 1.6 }}>
                    This document was analysed with all checks enabled.<br />
                    Configure specific checks on the upload screen and<br />
                    re-upload to record a custom configuration.
                  </div>
                </div>
              )}
            </div>
            {/* Footer */}
            <div style={{ padding: '12px 20px', borderTop: '1px solid #f3f4f6', display: 'flex', justifyContent: 'flex-end' }}>
              <button onClick={() => setDetectionOpen(false)}
                style={{ padding: '8px 20px', borderRadius: 8, border: 'none', background: '#44ace4', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Comment modal ── */}
      {commentOpen && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 200,
          background: 'rgba(0,0,0,0.4)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: 16,
        }}>
          <div style={{
            background: '#fff', borderRadius: 12, width: '100%', maxWidth: 460,
            boxShadow: '0 20px 60px rgba(0,0,0,0.22)', overflow: 'hidden',
          }}>
            {/* Modal header */}
            <div style={{ padding: '16px 20px 12px', borderBottom: '1px solid #f3f4f6', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontSize: 15, fontWeight: 800, color: '#111827' }}>Add Comment</div>
                <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 2 }}>Analyst note for {docName}</div>
              </div>
              <button onClick={() => setCommentOpen(false)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: '#9ca3af', lineHeight: 1, padding: 4 }}>×</button>
            </div>
            {/* Textarea */}
            <div style={{ padding: '16px 20px' }}>
              <textarea
                value={commentText}
                onChange={e => setCommentText(e.target.value)}
                placeholder="Add your analyst notes here — observations, concerns, or decisions…"
                rows={5}
                style={{
                  width: '100%', padding: '10px 12px', borderRadius: 8,
                  border: '1px solid #d1d5db', fontSize: 13, lineHeight: 1.6,
                  resize: 'vertical', outline: 'none', color: '#111827',
                  boxSizing: 'border-box', fontFamily: 'inherit',
                }}
                autoFocus
              />
            </div>
            {/* Footer */}
            <div style={{ padding: '0 20px 16px', display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button onClick={() => setCommentOpen(false)}
                style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#f9fafb', fontSize: 13, fontWeight: 600, color: '#6b7280', cursor: 'pointer' }}>
                Cancel
              </button>
              <button onClick={handleSaveComment} disabled={commentSaving}
                style={{
                  padding: '8px 20px', borderRadius: 8, border: 'none',
                  background: commentSaved ? '#16a34a' : '#44ace4',
                  color: '#fff', fontSize: 13, fontWeight: 700, cursor: commentSaving ? 'wait' : 'pointer',
                  display: 'flex', alignItems: 'center', gap: 6, transition: 'background 0.2s',
                }}>
                {commentSaved ? '✓ Saved!' : commentSaving ? 'Saving…' : <><FiSave size={13} /> Save Comment</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Top bar */}
      <div className="top-bar">
        <button className="back-btn" onClick={() => navigate(-1)}><FiArrowLeft /></button>
        <div className="top-bar-center">
          <FiFileText className="topbar-file-icon" />
          <span className="doc-name">{docName || 'Document'}</span>
          <span className="doc-format-tag">{fileFormat}</span>
          {qrCount > 0 && (
            <span style={{
              fontSize: 10, fontWeight: 700, padding: '2px 7px',
              background: '#ede9fe', color: '#7c3aed',
              border: '1px solid #ddd6fe', borderRadius: 20,
            }}>
              {qrCount} QR/Barcode{qrCount > 1 ? 's' : ''}
            </span>
          )}
        </div>
        <div className="top-bar-right" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {/* Check Detection Settings button */}
          <button
            onClick={() => setDetectionOpen(true)}
            style={{
              display: 'flex', alignItems: 'center', gap: 5,
              padding: '6px 12px', borderRadius: 7,
              border: '1px solid #e5e7eb', background: '#fff',
              fontSize: 12, fontWeight: 600, color: '#374151', cursor: 'pointer',
              transition: 'all 0.15s',
            }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = '#44ace4'; e.currentTarget.style.color = '#44ace4'; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = '#e5e7eb'; e.currentTarget.style.color = '#374151'; }}
          >
            <FiSettings size={13} />
            Detection Settings
          </button>

          {/* Add Comment button */}
          <button
            onClick={() => setCommentOpen(true)}
            style={{
              display: 'flex', alignItems: 'center', gap: 5,
              padding: '6px 12px', borderRadius: 7,
              border: '1px solid #e5e7eb', background: '#fff',
              fontSize: 12, fontWeight: 600, color: '#374151', cursor: 'pointer',
              transition: 'all 0.15s',
            }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = '#44ace4'; e.currentTarget.style.color = '#44ace4'; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = '#e5e7eb'; e.currentTarget.style.color = '#374151'; }}
          >
            <FiMessageSquare size={13} />
            {results?.user_comments ? 'Edit Comment' : 'Add Comment'}
          </button>

          {/* Mark for Review / Under Review / status toggle */}
          {docStatus === 'marked_for_review' ? (
            /* Already under review — show green "Under Review" with click to unmark */
            <button
              onClick={() => handleStatusChange('pending')}
              disabled={statusSaving}
              title="Click to remove from review"
              style={{
                display: 'flex', alignItems: 'center', gap: 5,
                padding: '6px 14px', borderRadius: 7, border: 'none',
                background: '#16a34a', color: '#fff',
                fontSize: 12, fontWeight: 700,
                cursor: statusSaving ? 'wait' : 'pointer',
                boxShadow: '0 0 0 3px #bbf7d0',
                transition: 'all 0.2s',
              }}
            >
              <FiBookmark size={13} />
              🔖 Under Review
            </button>
          ) : (
            /* Not yet marked — show neutral "Mark for Review" */
            <button
              onClick={() => handleStatusChange('marked_for_review')}
              disabled={statusSaving}
              style={{
                display: 'flex', alignItems: 'center', gap: 5,
                padding: '6px 12px', borderRadius: 7,
                border: '1px solid #e5e7eb', background: '#fff',
                color: '#374151',
                fontSize: 12, fontWeight: 600,
                cursor: statusSaving ? 'wait' : 'pointer',
                transition: 'all 0.15s',
              }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = '#16a34a'; e.currentTarget.style.color = '#16a34a'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = '#e5e7eb'; e.currentTarget.style.color = '#374151'; }}
            >
              <FiBookmark size={13} />
              Mark for Review
            </button>
          )}

          {/* Duplicate invoice button — only when duplicates exist */}
          {(() => {
            const dupIds   = results?.features_capture?._duplicate_invoice_ids || [];
            const dupNames = results?.features_capture?._duplicate_invoice_names || [];
            const invNum   = results?.features_capture?._duplicate_invoice_number || '';
            if (!dupIds.length) return null;
            return (
              <button
                onClick={() => setDupOpen(true)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 5,
                  padding: '6px 12px', borderRadius: 7,
                  border: 'none', background: '#fef3c7',
                  color: '#92400e', fontSize: 12, fontWeight: 700,
                  cursor: 'pointer', animation: 'dupPulse 2s ease-in-out infinite',
                }}
              >
                ⚠ Duplicate Invoice ({dupIds.length})
              </button>
            );
          })()}

          {/* Risk badge */}
          <StatusBadge value={riskLevel} />
        </div>
      </div>

      {/* ── Duplicate invoice modal ── */}
      {dupOpen && (() => {
        const dupIds   = results?.features_capture?._duplicate_invoice_ids || [];
        const dupNames = results?.features_capture?._duplicate_invoice_names || [];
        const invNum   = results?.features_capture?._duplicate_invoice_number || '';
        return (
          <div style={{
            position: 'fixed', inset: 0, zIndex: 200,
            background: 'rgba(0,0,0,0.4)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
          }}
            onClick={e => { if (e.target === e.currentTarget) setDupOpen(false); }}
          >
            <div style={{
              background: '#fff', borderRadius: 12, width: '100%', maxWidth: 460,
              boxShadow: '0 20px 60px rgba(0,0,0,0.22)', overflow: 'hidden',
            }}>
              {/* Header */}
              <div style={{ padding: '16px 20px 12px', borderBottom: '1px solid #f3f4f6', background: '#fffbeb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ fontSize: 15, fontWeight: 800, color: '#92400e' }}>
                    ⚠ Duplicate Invoice Detected
                  </div>
                  <div style={{ fontSize: 12, color: '#b45309', marginTop: 2 }}>
                    Invoice <strong>{invNum}</strong> already exists in {dupIds.length} previous document{dupIds.length > 1 ? 's' : ''}
                  </div>
                </div>
                <button onClick={() => setDupOpen(false)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 20, color: '#9ca3af', lineHeight: 1, padding: 4 }}>×</button>
              </div>
              {/* List */}
              <div style={{ padding: '12px 20px', maxHeight: 340, overflowY: 'auto' }}>
                <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                  Matching documents
                </div>
                {dupIds.map((id, i) => (
                  <div
                    key={id}
                    onClick={() => { setDupOpen(false); navigate(`/dashboard/analysis/${id}`); }}
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      padding: '10px 12px', borderRadius: 8,
                      border: '1px solid #e5e7eb', background: '#fff',
                      marginBottom: 8, cursor: 'pointer',
                      transition: 'all 0.15s',
                    }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = '#f59e0b'; e.currentTarget.style.background = '#fffbeb'; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = '#e5e7eb'; e.currentTarget.style.background = '#fff'; }}
                  >
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>
                        {dupNames[i] || `Document #${id}`}
                      </div>
                      <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>ID: {id}</div>
                    </div>
                    <span style={{ fontSize: 11, color: '#44ace4', fontWeight: 700 }}>View →</span>
                  </div>
                ))}
              </div>
              {/* Footer */}
              <div style={{ padding: '10px 20px', borderTop: '1px solid #f3f4f6', textAlign: 'right' }}>
                <button onClick={() => setDupOpen(false)}
                  style={{ padding: '7px 20px', borderRadius: 8, border: 'none', background: '#44ace4', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                  Close
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      <div className="content-area">

        {/* ══ LEFT SIDEBAR ═════════════════════════════════════════════════ */}
        <div className="sidebar">
          <PanelToggle
            value={leftTab}
            onChange={setLeftTab}
            options={[
              { value: 'features', label: 'Fields',   icon: <FiPackage style={{ fontSize: 11 }} /> },
              { value: 'metadata', label: 'Metadata', icon: <FiInfo    style={{ fontSize: 11 }} /> },
            ]}
          />

          <SCard title="Risk overview" style={{ marginTop: 8 }}>
            <RiskScoreRing score={riskScore} />
            <div style={{ height: 1, background: '#e5e7eb', margin: '10px 0' }} />
            <InfoRow icon={<FiHash />}     label="Document ID" value={results?.id ? `#${results.id}` : null} />
            <InfoRow icon={<FiFileText />} label="File name"   value={docName} />
            <InfoRow icon={<FiActivity />} label="Format"      value={fileFormat} />
            <InfoRow icon={<FiClock />}    label="Uploaded"    value={formatDate(results?.created_at)} />
          </SCard>

          {leftTab === 'features' ? (
            <SCard title="Extracted fields" style={{ marginTop: 8 }}>
              <FeaturesPanel
                features={results?.features_capture}
                clientFeatures={results?.client_specific_feature_capture}
              />
            </SCard>
          ) : (<>
            <SCard title="File metadata" style={{ marginTop: 8 }}>
              <MetadataPanel metadata={metadata} />
            </SCard>

            {/* ── Detection flags snapshot ── */}
            {results?.detection_flags && typeof results.detection_flags === 'object' && Object.keys(results.detection_flags).length > 0 && (
              <SCard title="Detection checks used" style={{ marginTop: 8 }} accent="#44ace4">
                <div style={{ fontSize: 10, color: '#9ca3af', marginBottom: 10, lineHeight: 1.5 }}>
                  These were the checks active when this document was analysed.
                  To change future settings use the gear icon on the upload screen.
                </div>
                {Object.entries(results.detection_flags).map(([key, enabled]) => {
                  const label = key
                    .split('_')
                    .map(w => w.charAt(0).toUpperCase() + w.slice(1))
                    .join(' ');
                  return (
                    <div key={key} style={{
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      padding: '5px 0', borderBottom: '1px solid #f3f4f6',
                    }}>
                      <span style={{ fontSize: 12, color: enabled ? '#111827' : '#9ca3af', fontWeight: enabled ? 500 : 400 }}>
                        {label}
                      </span>
                      <span style={{
                        fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 20,
                        background: enabled ? '#dcfce7' : '#f3f4f6',
                        color:      enabled ? '#15803d' : '#9ca3af',
                      }}>
                        {enabled ? 'On' : 'Off'}
                      </span>
                    </div>
                  );
                })}
              </SCard>
            )}

            {/* ── Comment preview (if any) ── */}
            {results?.user_comments && (
              <SCard title="Analyst comment" style={{ marginTop: 8 }} accent="#7c3aed">
                <p style={{ fontSize: 12, color: '#374151', lineHeight: 1.65, margin: 0, whiteSpace: 'pre-wrap' }}>
                  {results.user_comments}
                </p>
                <button
                  onClick={() => setCommentOpen(true)}
                  style={{ marginTop: 8, fontSize: 11, color: '#44ace4', fontWeight: 600, background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                  Edit →
                </button>
              </SCard>
            )}
          </>)}
        </div>

        {/* ══ CENTER ═══════════════════════════════════════════════════════ */}
        <div className="pdf-viewer">
          {fileType === 'pdf' ? (
            <PDFViewerWithBoxes
              fileUrl={fileUrl}
              issues={issues}
              qrBarcodes={qrBarcodes}
              activeIssueIdx={activeIdx}
              activeQrBox={activeQrBox}
              onBoxHover={handleBoxHover}
              onQrHover={handleQrHover}
              visibleSeverities={visibleSeverities}
            />
          ) : (
            <ImageViewer
              src={fileUrl}
              issues={issues}
              qrBarcodes={qrBarcodes}
              activeIssueIdx={activeIdx}
              activeQrBox={activeQrBox}
              onBoxHover={handleBoxHover}
              onQrHover={handleQrHover}
              visibleSeverities={visibleSeverities}
            />
          )}
        </div>

        {/* ══ RIGHT PANEL ══════════════════════════════════════════════════ */}
        <div className="right-panel">
          <div className="right-panel-header" style={{ flexDirection: 'column', gap: 8, alignItems: 'stretch' }}>
            {/* Toggle: Issues / QR */}
            <PanelToggle
              value={rightTab}
              onChange={setRightTab}
              options={[
                { value: 'issues', label: 'Issues', icon: <FiAlertTriangle style={{ fontSize: 11 }} />, badge: issues.length },
                { value: 'qr',     label: 'QR / Barcode', icon: <span style={{ fontSize: 11 }}>▦</span>, badge: qrCount },
              ]}
            />
          </div>

          {/* ── Risk Summary — sticky accordion OUTSIDE the scroll area ── */}
          {rightTab === 'issues' && summary && (
            <div style={{ flexShrink: 0, padding: '0 0 0 0', borderBottom: '1px solid #f3f4f6' }}>
              <div style={{ background: '#faf5ff', borderRadius: 0, overflow: 'hidden' }}>
                <button
                  onClick={() => setSummaryOpen(v => !v)}
                  style={{
                    width: '100%', padding: '9px 12px',
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    background: 'none', border: 'none', cursor: 'pointer',
                  }}
                >
                  <span style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 10, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.5px', color: '#7c3aed' }}>
                    <FiZap style={{ fontSize: 11 }} /> Risk Summary
                  </span>
                  <span style={{ fontSize: 13, color: '#7c3aed', lineHeight: 1, fontWeight: 700 }}>
                    {summaryOpen ? '▲' : '▼'}
                  </span>
                </button>
                {summaryOpen && (
                  <div style={{ padding: '0 12px 10px', borderTop: '1px solid #ede9fe' }}>
                    <p style={{ fontSize: 11, lineHeight: 1.65, color: '#374151', margin: 0 }}>{summary}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="issues-scroll">
            {rightTab === 'issues' ? (<>

              {/* Severity toggle tiles — click to show/hide that risk level */}
              {issues.length > 0 && (
                <>
                  <div style={{ fontSize: 9, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 4 }}>
                    Filter by severity — click to toggle
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 5, marginBottom: 10 }}>
                    {['critical','high','medium','low'].map(sev => {
                      const count = issues.filter(i => i.severity === sev).length;
                      if (!count) return null;
                      const cfg  = getCfg(sev);
                      const isOn = visibleSeverities.has(sev);
                      return (
                        <div
                          key={sev}
                          onClick={() => setVisibleSeverities(prev => {
                            const next = new Set(prev);
                            if (next.has(sev)) {
                              if (next.size > 1) next.delete(sev);
                            } else {
                              next.add(sev);
                            }
                            return next;
                          })}
                          title={isOn ? `Hide ${sev} — also hides bounding boxes` : `Show ${sev} — also shows bounding boxes`}
                          style={{
                            background:    isOn ? cfg.bg    : '#f3f4f6',
                            border:        `2px solid ${isOn ? cfg.color : '#e5e7eb'}`,
                            borderRadius:  8,
                            padding:       '8px 4px 6px',
                            textAlign:     'center',
                            cursor:        'pointer',
                            transition:    'all 0.18s',
                            opacity:       isOn ? 1 : 0.4,
                            userSelect:    'none',
                            position:      'relative',
                            boxShadow:     isOn ? `0 0 0 1px ${cfg.color}33` : 'none',
                          }}
                        >
                          <div style={{ fontSize: 18, fontWeight: 900, color: isOn ? cfg.color : '#9ca3af', lineHeight: 1 }}>
                            {count}
                          </div>
                          <div style={{ fontSize: 9, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.4px', color: isOn ? cfg.color : '#9ca3af', marginTop: 3 }}>
                            {sev}
                          </div>
                          {/* Eye icon — shows toggle state */}
                          <div style={{ marginTop: 4, fontSize: 10, color: isOn ? cfg.color : '#9ca3af' }}>
                            {isOn ? '👁' : '🚫'}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}

              {/* Issue cards — filtered by visibleSeverities */}
              {issues.length > 0
                ? issues
                    .filter(issue => visibleSeverities.has(issue.severity))
                    .map((issue, _filtIdx) => {
                    // Keep original index for cross-highlighting with the PDF viewer
                    const idx    = issues.indexOf(issue);
                    const cfg    = getCfg(issue.severity);
                    const active = activeIdx === idx;
                    return (
                      <div
                        key={idx}
                        ref={el => issueRefs.current[idx] = el}
                        onMouseEnter={() => handleIssueHover(idx)}
                        onMouseLeave={handleIssueLeave}
                        className={`issue-card severity--${issue.severity} ${active ? 'issue-card--active' : ''}`}
                        style={{ borderLeft: `3px solid ${cfg.color}`, cursor: issue.bounding_box ? 'pointer' : 'default', outline: active ? `2px solid ${cfg.color}` : 'none', outlineOffset: 1 }}
                      >
                        <div className="issue-card-header">
                          <span className="issue-source">
                            {formatLabel(issue.issue_type)}
                            {issue.bounding_box && (
                              <span style={{ display: 'inline-block', marginLeft: 5, fontSize: 9, padding: '1px 5px', background: '#e0f2fe', color: '#0284c7', borderRadius: 20, fontWeight: 700, verticalAlign: 'middle' }}>LOCATED</span>
                            )}
                          </span>
                          <StatusBadge value={issue.severity} size="sm" />
                        </div>
                        <p className="issue-message">{issue.explanation}</p>
                        {(issue.tags || []).length > 0 && (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 3, marginBottom: 4 }}>
                            {issue.tags.map((tag, ti) => (
                              <span key={ti} style={{ display: 'inline-block', fontSize: 10, fontWeight: 600, background: 'rgba(0,0,0,0.05)', color: '#6b7280', padding: '2px 7px', borderRadius: 20, textTransform: 'uppercase', letterSpacing: '0.3px', alignSelf: 'flex-start' }}>
                                {tag}
                              </span>
                            ))}
                          </div>
                        )}
                        {issue.risk_score != null && (
                          <div style={{ fontSize: 10, color: '#9ca3af', textAlign: 'right' }}>
                            Score: <strong style={{ color: cfg.color }}>{issue.risk_score}</strong>
                          </div>
                        )}
                      </div>
                    );
                  })
                : issues.length > 0
                  ? (
                    <div style={{ textAlign: 'center', color: '#9ca3af', padding: '24px 0', fontSize: 12 }}>
                      No {[...visibleSeverities].join(', ')} issues to show.<br/>
                      <button onClick={() => setVisibleSeverities(new Set(['critical','high','medium','low']))}
                        style={{ marginTop: 8, fontSize: 11, color: '#44ace4', fontWeight: 600, background: 'none', border: 'none', cursor: 'pointer' }}>
                        Show all
                      </button>
                    </div>
                  )
                  : <div className="no-issues"><FiCheckCircle /><p>No issues detected</p></div>
              }

            </>) : (
              /* QR / Barcode tab */
              <QRBarcodePanel
                qrBarcodes={qrBarcodes}
                onHoverBox={(key, _bb) => setActiveQrBox(key)}
                activeBoxIdx={activeQrBox}
              />
            )}
          </div>
        </div>

      </div>
    </div>
  );
};

export default DocumentAnalysis;